import { useMemo } from 'react'
import {
  Trash2,
  Map as MapIcon,
  Route as RouteIcon,
  AlertCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { useScenarios } from '@/hooks/useScenarios'
import type { ActionType, PlaybackScenario } from '@/types'
import { formatDateTime } from '@/common/routeUtil'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import RouteList from './RouteList'

// アクションごとの表示色（ドット表示用）
const ACTION_COLOR: Record<ActionType, string> = {
  move: '#9ca3af',
  move_loaded: '#8b5cf6',
  pickup: '#3b82f6',
  place: '#f97316',
  wait: '#a855f7',
  wait_resume: '#a855f7',
}

function actionCounts(scenario: PlaybackScenario) {
  const counts = {
    move: 0,
    move_loaded: 0,
    pickup: 0,
    place: 0,
    wait: 0,
    wait_resume: 0,
  }
  for (const r of scenario.routes) counts[(r.action as ActionType) || 'move']++
  return counts
}

export type BrowseTab = 'scenarios' | 'routes'

interface BrowseSidebarProps {
  s: ReturnType<typeof useScenarios>
  /** 選択中のタブ（マップ上のツールバーと連動するため親が管理する） */
  tab: BrowseTab
  onTabChange: (tab: BrowseTab) => void
}

export default function BrowseSidebar({
  s,
  tab,
  onTabChange,
}: BrowseSidebarProps) {
  const { t } = useTranslation()

  // いずれかのシナリオで使われているルートの mapId 集合
  const usedMapIds = useMemo(() => {
    const set = new Set<string>()
    for (const sc of s.scenarios) for (const r of sc.routes) set.add(r.mapId)
    return set
  }, [s.scenarios])

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* タブ（ルート → シナリオの順） */}
      <div className="flex items-stretch border-b border-gray-200 bg-gray-50">
        <button
          onClick={() => onTabChange('routes')}
          className={`flex-1 px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
            tab === 'routes'
              ? 'border-blue-500 text-blue-600 bg-white'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('scenarios.routes')} ({s.routes.length})
        </button>
        <button
          onClick={() => onTabChange('scenarios')}
          className={`flex-1 px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
            tab === 'scenarios'
              ? 'border-blue-500 text-blue-600 bg-white'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('scenarios.scenario')} ({s.scenarios.length})
        </button>
      </div>

      {tab === 'scenarios' ? (
        <>
          {/* 新規作成 / 編集はマップ上のフローティングツールバー */}
          <div className="flex items-center px-3 py-2 border-b">
            <span className="text-xs text-gray-500">
              {t('scenarios.count', {
                count: s.scenarios.length,
                max: s.maxScenarios,
              })}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {s.scenarios.length === 0 ? (
              <div className="text-gray-400 text-center py-6 text-xs">
                {t('scenarios.empty')}
              </div>
            ) : (
              <ul className="flex flex-col gap-1 select-none">
                {s.scenarios.map((scenario, index) => {
                  const thumbnail = s.thumbnails.get(scenario.id)
                  const counts = actionCounts(scenario)
                  const isSelected = s.selectedScenarioIds.has(scenario.id)
                  const isUnavailable = s.unavailableScenarioIds.has(
                    scenario.id
                  )
                  return (
                    <li
                      key={scenario.id}
                      onClick={(e) =>
                        s.selectScenarioAt(index, {
                          ctrl: e.ctrlKey || e.metaKey,
                          shift: e.shiftKey,
                        })
                      }
                      className={`group flex items-center gap-2 p-1.5 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-orange-50 border-orange-400'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {/* サムネイル。ルートが削除済みの場合は画像の代わりに警告アイコンを表示 */}
                      <div
                        className={`h-11 w-11 flex-shrink-0 border rounded overflow-hidden flex items-center justify-center ${
                          isUnavailable
                            ? 'border-red-200 bg-red-50'
                            : 'border-gray-200 bg-gray-100'
                        }`}
                      >
                        {isUnavailable ? (
                          <AlertCircle size={16} className="text-red-500" />
                        ) : thumbnail ? (
                          <img
                            src={thumbnail}
                            alt={`#${index + 1}`}
                            className="w-full h-full object-contain"
                            draggable={false}
                          />
                        ) : (
                          <MapIcon size={16} className="text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-800 truncate">
                          {scenario.title?.trim() ||
                            t('scenarios.untitled', { index: index + 1 })}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] text-gray-400">
                            #{index + 1}
                          </span>
                          <span className="flex items-center gap-0.5 text-[11px] text-gray-500">
                            <RouteIcon size={11} className="text-gray-400" />
                            {scenario.routes.length}
                          </span>
                          <span
                            className="flex items-center gap-0.5"
                            title={t('scenarios.actionSummary', counts)}
                          >
                            {scenario.routes.map((r, i) => (
                              <span
                                key={i}
                                className="w-1.5 h-1.5 rounded-full"
                                style={{
                                  backgroundColor:
                                    ACTION_COLOR[
                                      (r.action as ActionType) || 'move'
                                    ],
                                }}
                              />
                            ))}
                          </span>
                        </div>
                        {scenario.createdAt && (
                          <div className="text-[11px] text-gray-400 leading-tight truncate">
                            {formatDateTime(scenario.createdAt)}
                          </div>
                        )}
                        {isUnavailable && (
                          <div className="text-[11px] text-red-600 leading-tight">
                            {t('scenarios.routeDeleted')}
                          </div>
                        )}
                      </div>
                      {/* 編集はマップ上のツールバー（1件選択時に有効） */}
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            s.handleRequestListDelete(scenario.id, index + 1)
                          }}
                          className="text-gray-400 hover:text-red-500 p-1"
                          aria-label="delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </>
      ) : (
        <>
          {/* 新規作成 / 編集はマップ上のフローティングツールバー */}
          <div className="flex items-center px-3 py-2 border-b">
            <span className="text-xs text-gray-500">
              {t('scenarios.count', {
                count: s.routes.length,
                max: s.maxPlaybackRoutes,
              })}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <RouteList
              map={s.map}
              routes={s.routes}
              thumbnails={s.thumbnails}
              isSelected={(route) => s.selectedRouteIds.has(route.id)}
              onSelectRoute={(_route, index, mods) =>
                s.selectRouteAt(index, mods)
              }
              renderUsageBadge={(route) =>
                usedMapIds.has(route.mapId) ? (
                  <span className="flex-shrink-0 bg-green-100 text-green-700 text-[10px] font-medium leading-none px-1.5 py-0.5 rounded">
                    {t('scenarios.usedInScenario')}
                  </span>
                ) : null
              }
            />
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={s.listDeleteTarget !== null}
        title={t('scenarios.deleteTitle')}
        message={
          s.isListDeleting ? (
            t('scenarios.deleting')
          ) : (
            <>
              <p>
                {t('scenarios.deleteConfirm', {
                  index: s.listDeleteTarget?.index,
                })}
              </p>
              <p className="mt-2 text-sm text-gray-500">
                {t('scenarios.deviceUpdateNote')}
              </p>
            </>
          )
        }
        isAlert={true}
        confirmDisabled={s.isListDeleting}
        onConfirm={s.handleConfirmListDelete}
        onCancel={s.handleCancelListDelete}
      />
    </div>
  )
}
